import { useQueryClient } from "@tanstack/react-query";
import { Space, Switch, Table, Typography } from "antd";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import PermissionCheck from "~Common/PermissionCheck";
import { CompetitorTypes } from "~enums/competitorTypes";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import { useDeletePlacing } from "~pages/FinalStandings/hooks/useDeletePlacing";
import { useFetchAllFinalPlacings } from "~pages/FinalStandings/hooks/useFetchAllFinalPlacings";
import { usePlacingTableColumns } from "~pages/FinalStandings/hooks/usePlacingTableColumns";
import type { SearchFilterParams } from "~pages/FinalStandings/types/searchFilterParams";
import type { SeasonEntrant } from "~pages/FinalStandings/types/seasonEntrant";
import type { SeasonEntity } from "~types/seasonEntity";
import type { SeasonPerson } from "~types/seasonPerson";

function getEntrantId(entrant: SeasonEntrant): string {
  if (!entrant) return "";
  if ((entrant as SeasonEntity).entityId !== undefined) {
    return (entrant as SeasonEntity).entityId;
  }
  if ((entrant as SeasonPerson).personId !== undefined) {
    return (entrant as SeasonPerson).personId;
  }
  return "";
}

const FinalStandingsPage = () => {
  const params: SearchFilterParams = {
    limit: "1000",
    offset: "0",
    include: ["persons", "entities"].join(","),
  };
  const { data, isLoading } = useFetchAllFinalPlacings(params);
  const { formatMessage } = useIntl();
  const [massEditMode, setMassEditMode] = useState(false);
  const queryClient = useQueryClient();
  const { sport, organizationId, seasonId } = useParams();

  const onMassEditModeChangedHandler = () => {
    setMassEditMode((prevChecked) => {
      if (prevChecked) {
        queryClient.invalidateQueries({
          queryKey: ["final-standings", sport, organizationId, seasonId],
        });
      }
      return !prevChecked;
    });
  };

  const { competitorType } = useSportConfiguration();

  const { mutateAsync: deletePlacing, isPending: isDeletePlacingPending } = useDeletePlacing();

  const columns = usePlacingTableColumns(massEditMode, deletePlacing);

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
        className={"mb-2"}
      >
        <Typography.Title level={4}> {formatMessage({ id: "finalStandings" })} </Typography.Title>
        <Space>
          <PermissionCheck
            permissionKeys={[
              competitorType === CompetitorTypes.Entity ? PermissionNames.SEASON_ENTITY_PLACING : PermissionNames.SEASON_PERSON_PLACING,
            ]}
            permissionOptions={[PermissionOperations.UPDATE, PermissionOperations.CREATE]}
          >
            <span style={{ marginRight: 5 }}>{formatMessage({ id: "edit.mode" })}</span>
            <Switch onChange={onMassEditModeChangedHandler} />
          </PermissionCheck>
        </Space>
      </div>
      <Table
        dataSource={data?.data || []}
        loading={isLoading || isDeletePlacingPending}
        size={"small"}
        rowKey={(entrant: SeasonEntrant) => getEntrantId(entrant)}
        pagination={false}
        columns={columns}
      />
    </div>
  );
};

export default FinalStandingsPage;
